/* eslint-disable no-param-reassign */
/* eslint-disable react/forbid-prop-types */
import { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { ResponsiveBar } from '@nivo/bar';

import CustomLegend from '../../utils/chartTools/createLegend';
import customTooltip from '../../utils/chartTools/handleCustomTooltip';
import colorSelector from '../../utils/formatCharts/handleColor';
import customAxis from '../../utils/formatCharts/formatAxis';
import { genData2DWithKeys, dataFiller } from '../../utils/formatCharts/handleData';
import { getBiggestTick } from '../../utils/formatCharts/getAxisTicks';
import { labelValues } from '../../utils/formatCharts/handleCustomValue';
// import { checkErrors } from '../../utils/chartTools/kpiMsg';
import ExtraLine from '../../utils/chartTools/ExtraLine';
import KpiStatus from '../../utils/chartTools/KpiStatus';
import useUpdateKey from '../../utils/chartTools/useUpdateKey';
import CustomLabel from './CustomLabel';

const labelPosHV = (
  posH, posV, lineBreak, value, styleConfig, hasColumns,
) => {
  const offsetTop = lineBreak ? 25 : 10;
  const offsetCenter = lineBreak ? 7 : 0;
  if (styleConfig.GroupModeControl === 'stacked' && hasColumns) return [posH, posV - offsetCenter];
  switch (styleConfig.LabelFormatControl) {
    case 'Superior':
      return [posH, -offsetTop];
    case 'Inferior':
      return [posH, 2 * posV + 10];
    case 'Direita':
      return [2 * posH, posV - offsetCenter];
    case 'Esquerda':
      return [0, posV - offsetCenter];
    case 'Dinamico':
      if (styleConfig.LayoutControl === 'horizontal') {
        if (value < 0) {
          return [styleConfig.ReverseControl?.checked ? 2 * posH : 0, posV - offsetCenter];
        } if (value > 0) {
          return [styleConfig.ReverseControl?.checked ? 0 : 2 * posH, posV - offsetCenter];
        } return [2 * posH, posV - offsetCenter];
      }
      if (value < 0) {
        return [posH, styleConfig.ReverseControl?.checked ? -offsetTop : 2 * posV + 10];
      } if (value > 0) {
        return [posH, styleConfig.ReverseControl?.checked ? 2 * posV + 10 : -offsetTop];
      } return [posH, posV - offsetCenter];
    default:
      return [posH, posV - offsetCenter];
  }
};

const emptyObj = {};

const Bar = ({
  data, styleConfig, isEdit, globalConfig = emptyObj, handleDrilldownClick = null,
}) => {
  const customKey = useUpdateKey(String(handleDrilldownClick));
  const [chartData, setChartData] = useState({ ready: false });
  const [filteredChartData, setFilteredChartData] = useState({ data: [], keys: [] });
  const [hiddenIds, setHiddenIds] = useState([]);
  useEffect(() => {
    setChartData(genData2DWithKeys(dataFiller(data, styleConfig), styleConfig));
  }, [
    data,
    styleConfig.DataFormat,
    styleConfig.DataPercentFormat,
    styleConfig.DataFormatAlt,
    styleConfig.DataPercentFormatAlt,
    styleConfig.DataAbsoluteFormatAlt,
    styleConfig.TotalLineControl,
    styleConfig.InvertVariation,
    styleConfig.ExtendDateColumn,
    styleConfig.ExtendDateLine,
    ...styleConfig.CustomSortControl.kpiCategories,
    styleConfig.SortValuesControl,
    styleConfig.CustomSortColumnControl.kpiCategories,
    styleConfig.SortColumnsControl,
  ]);

  const dataFrom = () => {
    if (styleConfig?.ColorByControl === 'keys') return 'id';
    if (styleConfig?.ColorByControl === 'indexes') return 'indexValue';
    if (chartData.keys.length > 1) return 'id';
    return 'indexValue';
  };

  useEffect(() => {
    if (chartData.ready) {
      setFilteredChartData(dataFrom() === 'indexValue' ? {
        data: chartData.data.filter((item) => !hiddenIds.includes(item.uid)),
        keys: chartData.keys,
      } : {
        data: chartData.data,
        keys: chartData.keys.filter((k) => !hiddenIds.includes(k)),
      });
    }
  }, [chartData, hiddenIds]);

  const biggestTick = useMemo(() => {
    if (styleConfig.GroupModeControl === 'stacked' || styleConfig.ExtraLineControl !== 'no-line') {
      return getBiggestTick(chartData?.biggests?.global || 0, styleConfig);
    } return getBiggestTick(chartData?.biggests?.categories || 0, styleConfig);
  }, [chartData, styleConfig.GroupModeControl, styleConfig.ExtraLineControl]);

  const createLabel = (label, x, y) => {
    const {
      LabelLineBreak,
      DataFormatAlt,
      EnableLabelMinControl,
      LabelMinPercentControl,
      DataFormat,
    } = styleConfig;
    let t = 100;
    if (DataFormat !== 'percentual') {
      t = chartData.keys.length > 1 ? label.data.value : chartData.total;
    }
    if (EnableLabelMinControl && (label.value * 100) / t < (LabelMinPercentControl ?? 0)) {
      return null;
    }
    let posH = x;
    let posV = y;
    let val = '';
    const val1 = label.data[label.id] ?? 0;
    const val2 = label.data[`${label.id}-value2`] ?? 0;
    const [valIni, valEnd] = labelValues(
      val1, val2, styleConfig,
    );
    if (LabelLineBreak && DataFormatAlt !== 'none') {
      [posH, posV] = labelPosHV(x, y, true, val1, styleConfig, data?.columns?.length > 1);
      val = (
        <>
          <tspan>{valIni}</tspan>
          <tspan x={posH} dy="1.2em">{valEnd}</tspan>
        </>
      );
    } else {
      [posH, posV] = labelPosHV(x, y, false, val1, styleConfig, data?.columns?.length > 1);
      val = `${valIni} ${valEnd}`.trim();
    }
    return <tspan y={posV} x={posH}>{val}</tspan>;
  };

  const createTooltip = (val) => customTooltip(val, 'bar', styleConfig, handleDrilldownClick);

  // eslint-disable-next-line react/prop-types
  const TotalLine = ({ bars, yScale, innerHeight }) => {
    // console.log(props);
    const byCategory = () => {
      const minMax = {};
      // eslint-disable-next-line react/prop-types
      const filtered = bars.reduce((aux, bar) => {
        const category = bar.data.data.label;
        minMax[category] = minMax[category] ?? { min: Infinity, max: 0 };
        minMax[category].min = Math.min(minMax[category]?.min, bar.x);
        minMax[category].max = Math.max(minMax[category]?.max, bar.x);
        aux[bar.data.data.label] = {
          ...bar,
          x: (minMax[category].min + minMax[category].max) / 2,
        };
        return aux;
      }, {});
      return Object.values(filtered);
    };

    return (
      <ExtraLine
        bars={byCategory()}
        rawBars={bars}
        yScale={yScale}
        innerHeight={innerHeight}
        dataKey="value"
        lineType={styleConfig.ExtraLineControl}
        tooltip={createTooltip}
        styleConfig={styleConfig}
        globalConfig={globalConfig}
      />
    );
  };

  return chartData.ready ? (
    <KpiStatus
      conditions={([
        [
          'NoLineSelected',
          data?.index?.[0] === 'Totais',
        ],
      ])}
    >
      <ResponsiveBar
        key={customKey}
        data={filteredChartData.data}
        keys={filteredChartData.keys}
        onClick={handleDrilldownClick ? ((dataPoint) => {
          handleDrilldownClick(dataPoint?.data?.uid, dataPoint?.id);
        }) : null}
        indexBy="label"
        // valueScale={{ type: styleConfig.ValueScaleControl || 'linear' }}
        /* GENERAL */
        margin={styleConfig?.Margin || {
          top: 50,
          right: 145,
          bottom: 55,
          left: 60,
        }}
        valueScale={{
          type: styleConfig.ValueScaleControl || 'linear',
          reverse: !!styleConfig?.ReverseControl?.checked || false,
          min: styleConfig?.MinValueControl?.checked ? 'auto' : (
            styleConfig?.MinValueControl?.value
          ),
          max: styleConfig.MaxValueControl?.checked ? biggestTick : (
            styleConfig.MaxValueControl?.value
          ),
          nice: styleConfig.MaxValueControl?.checked && styleConfig.DataFormat !== 'percentual',
        }}
        groupMode={styleConfig.GroupModeControl || 'stacked'}
        layout={styleConfig.LayoutControl || 'vertical'}
        // reverse={!!styleConfig?.ReverseControl?.checked || false}
        // minValue={styleConfig?.MinValueControl?.checked
        //   ? 'auto' : styleConfig?.MinValueControl?.value}
        // maxValue={styleConfig.MaxValueControl?.checked
        //   ? biggestTick : styleConfig.MaxValueControl?.value}
        padding={styleConfig?.PaddingControl?.value || 0.1}
        innerPadding={styleConfig?.InnerPaddingControl?.value || 0}
        /* BORDER */
        borderRadius={styleConfig?.BorderRadiusControl?.value || 5}
        borderWidth={styleConfig?.BorderWidthControl?.value || 0}
        borderColor={
          styleConfig.BorderColorControl
            ? styleConfig.BorderColorControl
            : { from: 'color', modifiers: [['darker', 1.6]] }
        }
        /* LABEL */
        // enableLabel={
        //   styleConfig.EnableLabelControl ? styleConfig.EnableLabelControl.checked : true
        // }
        enableLabel={false}
        labelSkipWidth={styleConfig.labelSkipWidth || 0}
        labelSkipHeight={styleConfig.labelSkipHeight || 0}
        labelTextColor={styleConfig.LabelTextColorControl}
        label={createLabel}
        axisTop={customAxis('top', styleConfig, globalConfig, styleConfig.LayoutControl === 'horizontal')}
        axisLeft={customAxis('left', styleConfig, globalConfig, styleConfig.LayoutControl !== 'horizontal')}
        axisBottom={customAxis('bottom', styleConfig, globalConfig, styleConfig.LayoutControl === 'horizontal')}
        axisRight={customAxis('right', styleConfig, globalConfig, styleConfig.LayoutControl !== 'horizontal')}
        /* TOOLTIP */
        isInteractive={styleConfig.InteractiveControl}
        tooltip={createTooltip}
        /* LEGEND */
        enableGridX={styleConfig?.EnableGridX ?? false}
        enableGridY={styleConfig?.EnableGridY ?? false}
        layers={[
          'grid',
          'axes',
          'bars',
          (dt) => (styleConfig.EnableLabelControl?.checked === false ? null : CustomLabel({
            data: dt.bars, styleConfig, globalConfig, content: createLabel,
          })),
          styleConfig.ExtraLineControl !== 'no-line' && styleConfig.LayoutControl !== 'horizontal' ? TotalLine : null,
          'markers',
          styleConfig?.LegendControl?.checked ? (dt) => (
            <CustomLegend
              chartDataAr={dataFrom() === 'id' ? chartData.keys : chartData.data}
              colorParts={dt.bars.reduce((aux, p) => {
                aux[p.data[dataFrom()]] = p.color;
                return aux;
              }, {})}
              sizes={{
                width: dt.innerWidth,
                height: dt.innerHeight,
              }}
              config={styleConfig}
              setToggleIds={setHiddenIds}
              isEdit={isEdit}
            />
          ) : null,
          'annotations',
        ]}
        /* STYLES/THEMES */
        colorBy={dataFrom()}
        colors={dataFrom() === 'id' ? colorSelector({
          dataLength: chartData.keys.length, config: styleConfig, colorKey: 'id', globalConfig,
        }) : colorSelector({
          dataLength: chartData.data.length, config: styleConfig, colorKey: 'indexValue', globalConfig,
        })}
        theme={{
          legends: {
            text: {
              fontSize: `${styleConfig?.LegendsFontSizeControl?.value}px`,
              fontFamily: `'${globalConfig?.useGlobalFont ? globalConfig.fontLegend : styleConfig?.LegendsFontFamilyControl?.value}', Arial`,
              fill: styleConfig?.LegendFontColor ?? '#999',
              fontWeight: styleConfig?.LegendBold ? 'bold' : 'normal',
              fontStyle: styleConfig?.LegendItalic ? 'italic' : 'normal',
            },
            hidden: {
              text: {
                textDecoration: 'line-through',
              },
            },
          },
          labels: {
            text: {
              fontSize: `${styleConfig?.LabelsFontSizeControl ? styleConfig?.LabelsFontSizeControl.value : 13}px`,
              fontFamily: `'${globalConfig?.useGlobalFont ? globalConfig.fontLabel : styleConfig?.LabelsFontFamilyControl?.value}', Arial`,
              transform: `translate(${styleConfig?.LabelTranslateX ? styleConfig?.LabelTranslateX : 0}px, ${styleConfig?.LabelTranslateY ? styleConfig?.LabelTranslateY : 0}px) rotate(${styleConfig?.LabelRotationControl ? styleConfig?.LabelRotationControl : 0}deg)`,
              transformOrigin: 'center center',
              transformBox: 'fill-box',
            },
          },
          axis: {
            legend: {
              text: {
                fontSize: styleConfig?.AxisGlobal?.legendFontSize,
                fontFamily: `'${globalConfig?.useGlobalFont ? globalConfig.fontAxis : styleConfig?.AxisGlobal?.fontFamily}', Arial`,
                fill: styleConfig?.AxisGlobal?.color,
              },
            },
            ticks: {
              text: {
                fontFamily: `'${globalConfig?.useGlobalFont ? globalConfig.fontAxis : styleConfig?.AxisGlobal?.fontFamily}', Arial`,
                fontSize: styleConfig?.AxisGlobal?.fontSize,
                fill: styleConfig?.AxisGlobal?.color,
              },
            },
          },
        }}
        /* ETC */
        animate={styleConfig.InteractiveControl ? !!styleConfig.Animate : false}
        motionConfig={styleConfig.InteractiveControl ? (styleConfig.MotionConfig || 'default') : 'default'}
        markers={styleConfig.ShowMarkerControl
          ? styleConfig.MarkersList?.reduce((aux, lines) => {
            if (lines.showLine) {
              aux.push({
                ...lines,
                textStyle: {
                  ...lines.textStyle,
                  fontFamily: `'${lines.textStyle.fontFamily}', Arial`,
                },
              });
            }
            return aux;
          }, []) : []}
      />
    </KpiStatus>
  ) : <></>;
};

Bar.propTypes = {
  data: PropTypes.objectOf(PropTypes.any).isRequired,
  styleConfig: PropTypes.objectOf(PropTypes.any).isRequired,
  isEdit: PropTypes.bool.isRequired,
  globalConfig: PropTypes.objectOf(PropTypes.any),
  handleDrilldownClick: PropTypes.func,
};

export default Bar;
